import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { webCardVariantClassName } from '@emovia/design-system';

import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';
import { Heading } from './heading';

const cardVariants = cva('flex flex-col gap-6 rounded-xl transition-colors', {
  variants: {
    variant: {
      default: webCardVariantClassName('default'),
      elevated: webCardVariantClassName('elevated'),
      outlined: `${webCardVariantClassName('outlined')} border-2`,
      flat: webCardVariantClassName('flat'),
      interactive: `${webCardVariantClassName('interactive')} cursor-pointer hover:border-primary/50 hover:shadow-md transition-all`,
    },
    status: {
      none: '',
      success: 'border-l-4 border-l-green-500',
      warning: 'border-l-4 border-l-amber-500',
      error: 'border-l-4 border-l-red-500',
      info: 'border-l-4 border-l-blue-500',
    },
    semantic: {
      none: '',
      primary: 'border-l-4 border-l-primary bg-primary/5',
      success: 'border-l-4 border-l-green-600 bg-green-50 dark:bg-green-950/30',
      warning: 'border-l-4 border-l-amber-600 bg-amber-50 dark:bg-amber-950/30',
      destructive: 'border-l-4 border-l-destructive bg-destructive/5',
      emergency: 'border-l-4 border-l-emergency bg-emergency/5',
    },
  },
  defaultVariants: {
    variant: 'default',
    status: 'none',
    semantic: 'none',
  },
});

type CardProps = React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants> & {
    isLoading?: boolean;
    naked?: boolean;
  };

function Card({
  className,
  variant,
  status,
  semantic,
  isLoading = false,
  children,
  ...props
}: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, status, semantic }), className)}
      {...props}
    >
      {isLoading ? <CardLoadingSkeleton /> : children}
    </div>
  );
}

function CardLoadingSkeleton() {
  return (
    <>
      <div data-slot="card-header" className="space-y-2 px-6 pt-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div data-slot="card-content" className="space-y-3 px-6">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
      <div data-slot="card-footer" className="px-6 pb-6">
        <Skeleton className="h-9 w-24" />
      </div>
    </>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 last:pb-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({
  className,
  headingLevel = 'h3',
  ...props
}: React.ComponentProps<'h3'> & { headingLevel?: 'h2' | 'h3' | 'h4' }) {
  return (
    <Heading
      level={headingLevel == 'h2' ? 2 : headingLevel == 'h4' ? 4 : 3}
      data-slot="card-title"
      className={cn('leading-none', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p data-slot="card-description" className={cn('text-muted-foreground', className)} {...props} />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6 first:pt-6 last:pb-6', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 pb-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
